import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import InvitationLinks, { type ShownLink } from '../../components/staff/InvitationLinks';
import leadsService, {
  type Lead,
  type LeadSource,
  type LeadStatus,
} from '../../services/leads.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { tokens } from '../../components/forms/tokens';
import { useMovablePopup } from '../../components/MovablePopup';
import { readerLocale } from '../../i18n/language';

const STATUS_OPTIONS: { value: LeadStatus | ''; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending review' },
  { value: 'invited', label: 'Invitation sent' },
  { value: 'converted', label: 'Converted to user' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'duplicate', label: 'Duplicate' },
];
const SOURCE_OPTIONS: { value: LeadSource | ''; label: string }[] = [
  { value: '', label: 'All sources' },
  { value: 'web_form', label: 'Web form' },
  { value: 'oauth_google', label: 'OAuth (Google)' },
  { value: 'imported', label: 'Bulk import' },
];

const STATUS_TONES: Record<LeadStatus, { bg: string; fg: string; label: string }> = {
  pending:   { bg: '#FEF3C7', fg: '#92400E', label: 'Pending' },
  invited:   { bg: '#DBEAFE', fg: '#1D4ED8', label: 'Invited' },
  converted: { bg: '#D1FAE5', fg: '#065F46', label: 'Converted' },
  rejected:  { bg: '#FEE2E2', fg: '#B91C1C', label: 'Rejected' },
  duplicate: { bg: '#F3F4F6', fg: '#374151', label: 'Duplicate' },
};

/**
 * Manager-facing leads queue. Filters on status / source / vendor;
 * detail modal lets manager approve (emit invitation) or reject
 * (PERMANENT block by UNIQUE constraint). Rejection requires
 * review_notes (min 5 chars) per the API contract.
 */
export default function ManagerLeads(): JSX.Element {
  const [items, setItems] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [shownLinks, setShownLinks] = useState<ShownLink[]>([]);

  const [statusFilter, setStatusFilter] = useState<LeadStatus | ''>('pending');
  const [sourceFilter, setSourceFilter] = useState<LeadSource | ''>('');
  const [vendorFilter, setVendorFilter] = useState('');

  const [detail, setDetail] = useState<Lead | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await leadsService.list({
        status: statusFilter || undefined,
        source: sourceFilter || undefined,
        vendor: vendorFilter.trim() || undefined,
        limit: 100,
      });
      setItems(res.items);
    } catch (e) {
      setErr(getApiErrorMessage(e, 'Could not load leads.'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sourceFilter, vendorFilter]);

  useEffect(() => {
    reload();
  }, [reload]);

  const leadsList = items.length === 0 ? (
    <p style={{ color: tokens.textMuted }}>No leads match these filters.</p>
  ) : (
    <div className="overflow-x-auto">
    <table style={tableStyle}>
      <thead>
        <tr>
          <th style={thStyle}>Email</th>
          <th style={thStyle}>Name</th>
          <th style={thStyle}>Source</th>
          <th style={thStyle}>Status</th>
          <th style={thStyle}>Vendor</th>
          <th style={thStyle}>Received</th>
          <th style={thStyle} aria-label="actions" />
        </tr>
      </thead>
      <tbody>
        {items.map((l) => {
          const tone = STATUS_TONES[l.status];
          return (
            <tr key={l.id}>
              <td style={tdStyle}>{l.email}</td>
              <td style={tdStyle}>{l.first_name} {l.last_name}</td>
              <td style={tdStyle}>{l.source}</td>
              <td style={tdStyle}>
                <span style={{
                  background: tone.bg, color: tone.fg,
                  padding: '2px 8px', borderRadius: 12, fontSize: 11,
                  fontWeight: 600,
                }}>
                  {tone.label}
                </span>
              </td>
              <td style={tdStyle}>{l.vendor_source ?? '—'}</td>
              <td style={tdStyle}>{new Date(l.created_at).toLocaleDateString(readerLocale())}</td>
              <td style={tdStyle}>
                <button type="button" onClick={() => setDetail(l)} style={btnSecondary}>
                  Review
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    </div>
  );

  return (
    <div style={{ padding: 24, color: tokens.textPrimary }}>
      <PageHeader
        title="Leads"
        subtitle="Strangers who requested access. Approve to send them an invitation; reject is permanent (the email is banned from re-submitting)."
      />

      {flash && <div style={okBoxStyle}>{flash}</div>}
      <InvitationLinks links={shownLinks} />
      {err && <div style={errBoxStyle}>{err}</div>}

      <div style={filterRowStyle}>
        <label style={lblStyle}>
          Status
          {/* */}<select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as LeadStatus | '')}
            style={inputStyle}
          >
            {[...STATUS_OPTIONS]
              .sort((a, b) => a.label.localeCompare(b.label))
              .map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
          </select>
        </label>
        <label style={lblStyle}>
          Source
          {/* */}<select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value as LeadSource | '')}
            style={inputStyle}
          >
            {[...SOURCE_OPTIONS]
              .sort((a, b) => a.label.localeCompare(b.label))
              .map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
          </select>
        </label>
        <label style={{ ...lblStyle, flex: 1 }}>
          Vendor contains
          {/* */}<input
            type="text"
            value={vendorFilter}
            onChange={(e) => setVendorFilter(e.target.value)}
            placeholder="e.g. BrokerX"
            style={inputStyle}
          />
        </label>
        <button type="button" onClick={() => reload()} style={btnSecondary}>
          Refresh
        </button>
      </div>

      {loading ? (
        <p style={{ color: tokens.textMuted }}>Loading…</p>
      ) : (
        leadsList
      )}

      {detail && (
        <LeadDetailModal
          lead={detail}
          onClose={() => setDetail(null)}
          onApproved={(msg, shown) => {
            setFlash(msg);
            setShownLinks(shown ? [shown] : []);
            setDetail(null);
            reload();
          }}
          onRejected={(msg) => { setFlash(msg); setDetail(null); reload(); }}
          onError={(msg) => setErr(msg)}
        />
      )}
    </div>
  );
}

interface ModalProps {
  lead: Lead;
  onClose: () => void;
  onApproved: (msg: string, shown: ShownLink | null) => void;
  onRejected: (msg: string) => void;
  onError: (msg: string) => void;
}

function LeadDetailModal({
  lead, onClose, onApproved, onRejected, onError,
}: Readonly<ModalProps>): JSX.Element {
  const popup = useMovablePopup<HTMLDivElement>();
  const [approveNotes, setApproveNotes] = useState('');
  const [rejectNotes, setRejectNotes] = useState('');
  const [confirmReject, setConfirmReject] = useState(false);
  const [busy, setBusy] = useState(false);

  const isPending = lead.status === 'pending';
  const fullName = useMemo(() => `${lead.first_name} ${lead.last_name}`, [lead]);

  const doApprove = async (): Promise<void> => {
    setBusy(true);
    try {
      const approved = await leadsService.approve(lead.id, approveNotes.trim() || undefined);
      onApproved(
        `Invitation sent to ${lead.email}.`,
        approved.invitation_link ? { who: lead.email, link: approved.invitation_link } : null,
      );
    } catch (e) {
      onError(getApiErrorMessage(e, 'Could not approve this lead.'));
    } finally {
      setBusy(false);
    }
  };

  const doReject = async (): Promise<void> => {
    if (rejectNotes.trim().length < 5) {
      onError('Rejection notes must be at least 5 characters (audit requirement).');
      return;
    }
    setBusy(true);
    try {
      await leadsService.reject(lead.id, rejectNotes.trim());
      onRejected(`${lead.email} permanently rejected.`);
    } catch (e) {
      onError(getApiErrorMessage(e, 'Could not reject this lead.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <dialog open style={overlayStyle} aria-modal="true">
      <div ref={popup.panelRef} style={{ ...modalStyle, ...popup.panelStyle }}>
        {popup.bar}
        <h2 style={{ margin: '0 0 12px', fontSize: 18 }}>{fullName}</h2>
        <dl style={dlStyle}>
          <dt style={dtStyle}>Email</dt>
          <dd style={ddStyle}>{lead.email}</dd>
          <dt style={dtStyle}>Phone</dt>
          <dd style={ddStyle}>{lead.phone ?? '—'}</dd>
          <dt style={dtStyle}>Source</dt>
          <dd style={ddStyle}>{lead.source}</dd>
          <dt style={dtStyle}>Vendor</dt>
          <dd style={ddStyle}>
            {lead.vendor_source ?? '—'}
            {lead.vendor_acquired_at && ` · acquired ${new Date(lead.vendor_acquired_at).toLocaleDateString(readerLocale())}`}
            {lead.vendor_cost !== null && ` · $${lead.vendor_cost}`}
          </dd>
          <dt style={dtStyle}>Submitted</dt>
          <dd style={ddStyle}>{new Date(lead.created_at).toLocaleString(readerLocale())}</dd>
          {lead.message && (
            <>
              <dt style={dtStyle}>Message</dt>
              <dd style={{ ...ddStyle, fontStyle: 'italic' }}>&quot;{lead.message}&quot;</dd>
            </>
          )}
          {lead.reviewer_name && (
            <>
              <dt style={dtStyle}>Reviewed by</dt>
              <dd style={ddStyle}>
                {lead.reviewer_name}
                {lead.reviewed_at && ` · ${new Date(lead.reviewed_at).toLocaleString(readerLocale())}`}
              </dd>
            </>
          )}
          {lead.review_notes && (
            <>
              <dt style={dtStyle}>Review notes</dt>
              <dd style={ddStyle}>{lead.review_notes}</dd>
            </>
          )}
        </dl>

        {isPending ? (
          <div style={{ display: 'grid', gap: 12, marginTop: 16 }}>
            <fieldset style={fsStyle}>
              <legend style={legendStyle}>Approve &amp; send invitation</legend>
              <textarea
                value={approveNotes}
                onChange={(e) => setApproveNotes(e.target.value)}
                rows={2}
                maxLength={2000}
                placeholder="Optional notes for the approval record."
                style={inputStyle}
              />
              <button type="button" onClick={doApprove} disabled={busy} style={btnApprove}>
                {busy ? 'Sending…' : 'Approve & invite'}
              </button>
            </fieldset>
            <fieldset style={fsStyle}>
              <legend style={legendStyle}>Reject (permanent)</legend>
              <p style={{ margin: '0 0 8px', fontSize: 12, color: tokens.errorText }}>
                ⚠ Rejection is PERMANENT. This email will never be allowed to
                resubmit through the form or appear in a bulk import.
              </p>
              <textarea
                value={rejectNotes}
                onChange={(e) => setRejectNotes(e.target.value)}
                rows={3}
                maxLength={2000}
                placeholder="Reason for rejection (min 5 chars, required for audit)."
                style={inputStyle}
              />
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
                <input
                  type="checkbox"
                  checked={confirmReject}
                  onChange={(e) => setConfirmReject(e.target.checked)}
                />
                {/* */}I confirm this is a permanent block.
              </label>
              <button
                type="button"
                onClick={doReject}
                disabled={busy || !confirmReject || rejectNotes.trim().length < 5}
                style={btnReject}
              >
                {busy ? 'Rejecting…' : 'Reject permanently'}
              </button>
            </fieldset>
          </div>
        ) : null}

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={btnSecondary} disabled={busy}>
            Close
          </button>
        </div>
      </div>
    </dialog>
  );
}

// Styles
const tableStyle: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse', fontSize: 13,
  color: tokens.textPrimary, marginTop: 12,
};
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px',
  borderBottom: `1px solid ${tokens.border}`, color: tokens.textMuted,
  fontSize: 11, textTransform: 'uppercase', letterSpacing: 1,
};
const tdStyle: React.CSSProperties = {
  padding: '8px 10px', borderBottom: `1px solid ${tokens.border}`,
};
const filterRowStyle: React.CSSProperties = {
  display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end',
  marginBottom: 12,
};
const lblStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11,
  color: tokens.textMuted, textTransform: 'uppercase', letterSpacing: 1,
};
const inputStyle: React.CSSProperties = {
  background: tokens.surfaceMuted, border: `1px solid ${tokens.border}`,
  borderRadius: 8, padding: '8px 10px',
  color: tokens.textPrimary, fontSize: 13,
};
const btnSecondary: React.CSSProperties = {
  background: tokens.surface, border: `1px solid ${tokens.border}`,
  color: tokens.textPrimary, borderRadius: 6, padding: '6px 12px',
  cursor: 'pointer', fontSize: 12,
};
const btnApprove: React.CSSProperties = {
  background: tokens.successBorder, color: tokens.navy,
  border: 'none', borderRadius: 6, padding: '8px 14px',
  cursor: 'pointer', fontWeight: 600,
};
const btnReject: React.CSSProperties = {
  background: tokens.errorBorder, color: '#fff',
  border: 'none', borderRadius: 6, padding: '8px 14px',
  cursor: 'pointer', fontWeight: 600,
};
const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(6,9,26,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 50, padding: 24,
  margin: 0, border: 'none', maxWidth: 'none', maxHeight: 'none',
  // A <dialog> is only as large as its content unless it is given a size.
  width: '100%', height: '100%',
};
const modalStyle: React.CSSProperties = {
  background: tokens.surface, borderRadius: 12, padding: 20,
  width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto',
  color: tokens.textPrimary,
};
const dlStyle: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: '120px 1fr', gap: '6px 12px',
  margin: 0, fontSize: 13,
};
const dtStyle: React.CSSProperties = {
  color: tokens.textMuted, fontSize: 11,
  textTransform: 'uppercase', letterSpacing: 0.5,
};
const ddStyle: React.CSSProperties = { margin: 0 };
const fsStyle: React.CSSProperties = {
  border: `1px solid ${tokens.border}`, borderRadius: 8,
  padding: 12, display: 'flex', flexDirection: 'column', gap: 8,
};
const legendStyle: React.CSSProperties = {
  color: tokens.textMuted, fontSize: 11,
  padding: '0 6px', textTransform: 'uppercase', letterSpacing: 1,
};
const okBoxStyle: React.CSSProperties = {
  background: tokens.successBg, border: `1px solid ${tokens.successBorder}`,
  color: tokens.successText, padding: 10, borderRadius: 8,
  marginBottom: 12, fontSize: 13,
};
const errBoxStyle: React.CSSProperties = {
  background: tokens.errorBg, border: `1px solid ${tokens.errorBorder}`,
  color: tokens.errorText, padding: 10, borderRadius: 8,
  marginBottom: 12, fontSize: 13,
};
